import type { BlockSerialized, BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/types/block';
import { isNil, randomUuid } from '#layers/base/public';
import { slotOutletBlockId } from '#layers/block-engine/utils/block/const';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';

export interface ProcessAddBlockOptions {
	editedBlock: BlockSerialized;
	blockId: string;
	parentChildId?: string | null;
	slot?: string;
	props?: Record<string, any>;
	/**
	 * Only for slot outlets
	 */
	slotName?: string;
	index?: number | null;
}

export function processAddBlock(options: ProcessAddBlockOptions): {
	newId: string;
	data: Partial<BlockSerialized>;
} {
	const {
		editedBlock,
		blockId,
		parentChildId = null,
		slot = 'default',
		props = {},
		index = null,
	} = options;

	if (isNil(parentChildId) && slot !== 'default') {
		throw new Error('You cannot specify a slot name for root children');
	}

	const newChild = blockId === slotOutletBlockId
		? {
				id: randomUuid(),
				props: {},
				slotName: options.slotName ?? 'default',
			} as BlockSerializedSlotOutlet
		: {
				id: randomUuid(),
				blockId,
				props,
				slots: {},
			} as BlockSerializedChild;

	const children = {
		...editedBlock.children,
		[newChild.id]: newChild,
	};

	if (isNil(parentChildId)) {
		const rootChildren = editedBlock.rootChildren?.slice() ?? [];

		if (isNil(index)) {
			rootChildren.push(newChild.id);
		}
		else {
			rootChildren.splice(index, 0, newChild.id);
		}

		// Root children
		return {
			newId: newChild.id,
			data: {
				children,
				rootChildren,
			},
		};
	}
	else {
		const parentChild = editedBlock.children?.[parentChildId];

		if (!parentChild) {
			throw new Error(`Parent child with id ${parentChildId} not found`);
		}

		let newParentChild: typeof parentChild;

		if (isBlockSerializedChild(parentChild)) {
			const slotChildren = parentChild.slots[slot]?.slice() ?? [];

			if (isNil(index)) {
				slotChildren.push(newChild.id);
			}
			else {
				slotChildren.splice(index, 0, newChild.id);
			}

			// Block
			newParentChild = {
				...parentChild,
				slots: {
					...parentChild.slots,
					[slot]: slotChildren,
				},
			};
		}
		else if (isBlockSerializedSlotOutlet(parentChild)) {
			const fallback = parentChild.fallback?.slice() ?? [];

			if (isNil(index)) {
				fallback.push(newChild.id);
			}
			else {
				fallback.splice(index, 0, newChild.id);
			}

			// Slot outlet
			newParentChild = {
				...parentChild,
				fallback,
			};
		}
		else {
			throw new Error(`Invalid parent child with id ${parentChildId}`);
		}

		return {
			newId: newChild.id,
			data: {
				children: {
					...children,
					[parentChildId]: newParentChild,
				},
			},
		};
	}
}
